<script setup lang="ts">
import type { InlineCardNodeViewProps } from "./types";
import type AxureInlineCard from "./well-known/AxureInlineCard.vue";
import { useQuery } from "@tanstack/vue-query";
import { NodeViewWrapper } from "@tiptap/vue-3";
import { SimplePopoverAnchor, SimplePopoverContent, SimplePopoverPortal, SimplePopoverRoot, SimplePopoverTrigger } from "ls/components/simple-popover";
import { queries } from "ls/features/bookmark/queries";
import LinkFrame from "ls/features/disko/links/LinkFrame.vue";
import { computed, ref, toRef } from "vue";
import LinkBox from "../LinkBox.vue";
import LinkToolbar from "../LinkToolbar.vue";
import LinkToolbarEdit from "../LinkToolbarEdit.vue";
import MaximizeOverlayContainer from "../MaximizeOverlayContainer.vue";
import { useLinkNode } from "../useLinkNode";
import { useWellKnownServiceMatch } from "./well-known/providers";

const props = defineProps<InlineCardNodeViewProps>();

const { isMaximized, showToolbar, isPointer, selected, handleLinkClick, close, handleUpdateOpen, isEditing } = useLinkNode(props);

const { data: urlMetadata } = useQuery(computed(() => queries.urlMetadata(props.node.attrs.url)));

const url = toRef(() => props.node.attrs.url);
const wellKnownServiceMatch = useWellKnownServiceMatch(url);

const wellKnownInlineCard = ref<InstanceType<typeof AxureInlineCard>>();
const text = computed(() => {
  if (wellKnownInlineCard.value && wellKnownInlineCard.value.label) {
    return wellKnownInlineCard.value.label as string;
  }
  if (urlMetadata.value && urlMetadata.value.meta?.title) {
    return urlMetadata.value.meta.title;
  }
  return props.node.attrs.text || props.node.attrs.url;
});

function handleUnlink() {
  const pos = props.getPos();
  if (pos === undefined) return false;

  props.editor
    .chain()
    .setNodeSelection(pos)
    .unlinkInlineCard(text.value)
    .focus()
    .run();
}
</script>

<template>
  <NodeViewWrapper
    as="a"
    :href="props.node.attrs.url"
    class="after:content-none!"
    @click="handleLinkClick"
  >
    <SimplePopoverRoot
      :open="showToolbar"
      :modal="false"
      @update:open="handleUpdateOpen"
    >
      <!-- use 1px anchor for popover content sizing to be minimal -->
      <SimplePopoverAnchor as="span" class="size-px" />
      <SimplePopoverTrigger as-child>
        <LinkBox
          variant="inline-card"
          :active="showToolbar"
          :pointer="isPointer"
          :selected="selected"
        >
          <component
            :is="wellKnownServiceMatch.inlineCard"
            v-if="wellKnownServiceMatch"
            ref="wellKnownInlineCard"
            v-bind="props"
            :match="wellKnownServiceMatch"
            :metadata="urlMetadata"
          />
          <template v-else-if="urlMetadata?.meta">
            <div v-if="urlMetadata.meta.faviconUrl" class="relative top-0.5 mr-1 inline-flex">
              <img class="inline size-4 flex-none" :src="urlMetadata.meta.faviconUrl">
            </div>
            <span>{{ props.node.attrs.text || urlMetadata.meta.title || props.node.attrs.url }}</span>
          </template>
          <template v-else>
            <span>{{ props.node.attrs.text || props.node.attrs.url }}</span>
          </template>
        </LinkBox>
        <span class="inline-block w-px" />
      </SimplePopoverTrigger>
      <SimplePopoverPortal>
        <SimplePopoverContent
          side="top"
          align="start"
          data-axure-bubble-menu-content
          @open-auto-focus.prevent
        >
          <LinkToolbarEdit
            v-if="isEditing"
            v-bind="props"
            @close="close"
          />
          <LinkToolbar
            v-else
            v-bind="props"
            v-model:maximized="isMaximized"
            v-model:edit-mode="isEditing"
            @unlink="handleUnlink"
          />
        </SimplePopoverContent>
      </SimplePopoverPortal>
    </SimplePopoverRoot>
    <!--
        Use teleport to break outside of the NodeViewWrapper.
        This is important to prevent any side-effects of being inside an <a> tag, e.g. styling or click events.
    -->
    <Teleport
      v-if="isMaximized"
      to="body"
    >
      <MaximizeOverlayContainer
        v-model="isMaximized"
      >
        <LinkFrame :url="props.node.attrs.url" :url-metadata="urlMetadata" />
      </MaximizeOverlayContainer>
    </Teleport>
  </NodeViewWrapper>
</template>
